<script lang="ts" setup>
import { useSidebar } from '../ui/sidebar'
import { NuxtLink } from '#components'
import { useAuthStore } from '~/store/authStore'

const localePath = useLocalePath()
const { t } = useI18n()

const user = await useAuthStore().getUser()

type SidebarLink = {
  label: string
  to?: string
  badge?: string
  icon?: string
  tooltip?: {
    text: string
  }
  children?: SidebarLink[]
}

const sideBarLinks: SidebarLink[] = [
  {
    label: t('Match center'),
    to: localePath('/'),
    icon: 'tabler-home',
    tooltip: {
      text: t('View ongoing and upcoming tournaments live'),
    },
  },
  {
    label: t('Play'),
    icon: 'tabler-play',
    to: localePath('/play'),
    children: [
      {
        label: t('Find tournaments'),
        to: localePath('/tournaments'),
        tooltip: {
          text: t('Find tournaments to play in'),
        },
      },
      {
        label: t('Quick game'),
        to: localePath('/play/offline'),
      },
      {
        label: t('Online lobby'),
        to: localePath('/play/lobby'),
      },
    ],
  },
  {
    label: t('Training'),
    icon: 'tabler-arrow-big-up',
    to: localePath('/play/training'),
    badge: 'BETA',
    children: [
      {
        label: t('Dashboard'),
        to: localePath('/play/training'),
      },
      {
        label: t('Practice Games'),
        to: localePath('/play/training/games'),
      },
      {
        label: t('Practice Stats'),
        to: localePath('/play/training/stats'),
      },
      ...(user?.coached_by
        ? [
            {
              label: t('My coach'),
              to: localePath('/play/training/coaching/my-coach'),
            },
          ]
        : []),
      ...(user?.is_coach
        ? [
            {
              label: t('My students'),
              to: localePath('/play/training/coaching/my-students'),
            },
          ]
        : []),
    ],
  },
  {
    label: t('My DartsSpace'),
    icon: 'tabler-user',
    to: localePath('/profile'),
    children: [
      {
        label: t('Match history'),
        to: localePath('/profile/my-matches'),
        tooltip: {
          text: t('View your match history'),
        },
      },
      {
        label: t('My tournaments'),
        to: localePath('/profile/my-tournaments'),
        tooltip: {
          text: t('Your future, completed and ongoing tournaments'),
        },
      },
      {
        label: t('My Stats'),
        to: localePath('/profile/my-stats'),
        tooltip: {
          text: t('Viw your stats and progress'),
        },
      },
      {
        label: t('Account Settings'),
        to: localePath('/profile/settings'),
      },
    ],
  },
  {
    label: t('Tournament Manager'),
    icon: 'tabler-list-check',
    to: localePath('/manage/'),
    children: [
      {
        label: t('Dashboard'),
        to: localePath('/manage/'),
      },
      {
        label: t('Tournaments'),
        to: localePath('/manage/tournaments'),
      },
      {
        label: t('Events & Locations'),
        to: localePath('/manage/events-locations'),
      },
      {
        label: t('Rankings'),
        to: localePath('/manage/rankings'),
      },
    ],
  },
]

// Create refs to track the open/closed state of each collapsible item
const collapsibleStates = useCookie<Record<string, boolean>>('sidebarstate', {
  default: getDefaultState,
})

// Initialize default states for each sidebar link with children
function getDefaultState(): Record<string, boolean> {
  const defaultState: Record<string, boolean> = {}
  sideBarLinks.forEach((item) => {
    if ((item.children?.length ?? 0) > 0) {
      // If not already set, default to open
      if (!(item.label in defaultState)) {
        defaultState[item.label] = true
      }
    }
  })

  return defaultState
}

// Function to get collapsible state or default to true if not set
const getCollapsibleState = (itemLabel: string): boolean => {
  return itemLabel in collapsibleStates.value ? collapsibleStates.value[itemLabel] ?? true : true
}

// Function to update collapsible state
const updateCollapsibleState = (itemLabel: string, isOpen: boolean) => {
  collapsibleStates.value[itemLabel] = isOpen
}

const { state, isMobile } = useSidebar()
</script>

<template>
  <UiSidebarGroup>
    <UiSidebarMenu>
      <template
        v-for="item in sideBarLinks"
        :key="`group-${item.label}`"
      >
        <UiCollapsible
          v-if="(item.children?.length ?? 0) > 0"
          v-model:open="collapsibleStates[item.label]"
          as-child
          :default-open="getCollapsibleState(item.label)"
          class="group/collapsible"
          @update:open="updateCollapsibleState(item.label, $event)"
        >
          <UiSidebarMenuItem>
            <UiCollapsibleTrigger as-child>
              <UiSidebarMenuButton
                :tooltip="item.label"
              >
                <NuxtLink
                  v-if="item.to && item.icon && state !== 'expanded'"
                  class="iconify"
                  :to="item.to"
                >
                  <Icon
                    v-if="item.icon"
                    :name="item.icon"
                  />
                </NuxtLink>
                <Icon
                  v-else-if="item.icon"
                  :name="item.icon"
                />

                <span>{{ item.label }}</span>
                <span
                  v-if="item.badge"
                  class="ml-0 py-0.5 px-1 text-success"
                  variant="success"
                >
                  {{ item.badge }}
                </span>
                <Icon
                  name="tabler:chevron-right"
                  class="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                />
              </UiSidebarMenuButton>
            </UiCollapsibleTrigger>
            <UiCollapsibleContent>
              <UiSidebarMenuSub>
                <UiSidebarMenuSubItem
                  v-for="child in item.children"
                  :key="child.label"
                >
                  <UiSheetClose
                    v-if="isMobile"
                    as-child
                  >
                    <UiSidebarMenuSubButton
                      :as="NuxtLink"
                      :to="child.to"
                    >
                      <span>{{ child.label }}</span>
                    </UiSidebarMenuSubButton>
                  </UiSheetClose>
                  <UiSidebarMenuSubButton
                    v-else
                    :as="NuxtLink"
                    :to="child.to"
                  >
                    <span>{{ child.label }}</span>
                  </UiSidebarMenuSubButton>
                </UiSidebarMenuSubItem>
              </UiSidebarMenuSub>
            </UiCollapsibleContent>
          </UiSidebarMenuItem>
        </UiCollapsible>

        <!-- non collapsible -->
        <UiSidebarMenuItem v-else>
          <UiSheetClose
            v-if="isMobile"
            as-child
          >
            <UiSidebarMenuButton
              :tooltip="item.label"
              :as="NuxtLink"
              :to="item.to"
            >
              <Icon
                v-if="item.icon"
                :name="item.icon"
              />
              <span>{{ item.label }}</span>
            </UiSidebarMenuButton>
          </UiSheetClose>
          <UiSidebarMenuButton
            v-else
            :tooltip="item.label"
            :as="NuxtLink"
            :to="item.to"
          >
            <Icon
              v-if="item.icon"
              :name="item.icon"
            />
            <span>{{ item.label }}</span>
          </UiSidebarMenuButton>
        </UiSidebarMenuItem>
      </template>
    </UiSidebarMenu>
  </UiSidebarGroup>
</template>
